import { useEffect, useRef, useState } from 'react';

import {
  displayNotification,
  useCompleteUploadMutation,
  useInitiateUploadMutation,
  useUploadMutation,
} from '../services';
import { CompleteUploadData, FileStatus, Upload } from '../models';
import { calculateChunkSize, getTomorrowsDate } from '../utils';
import {
  MutationResult,
  classifyMutationResult,
  extractResponsePayload,
} from '../utils/api-response';

/**
 * Chunked (S3 multipart) upload driver.
 *
 * FAILURE MODEL — why this file is written the way it is
 * ------------------------------------------------------
 * core-api answers a FAILED operation with HTTP 200 and a non-'0' errorCode in
 * the body, so `.unwrap()` does NOT reject on it. The previous implementation
 * unwrapped each step and then read straight through the envelope, which meant:
 *
 *   - a failed chunk reply pushed `undefined` into the multipart manifest and
 *     the loop marched on, so the file was assembled from a manifest that S3
 *     could never satisfy (corruption-grade);
 *   - a server-REJECTED completeUpload was marked `Completed` unless its body
 *     happened to equal one literal message;
 *   - a failed initiateUpload still entered the chunk loop with an undefined
 *     uploadId.
 *
 * Every step now goes through the canonical classifier in
 * `src/utils/api-response.ts` and FAILS CLOSED: anything that is not provably a
 * body-level success (`errorCode === '0'`) carrying the value we need aborts
 * the file with {@link FileStatus.Failed}, stops the chunk loop, and leaves the
 * manifest untouched. A transport failure (network blip, timeout, non-2xx) is
 * classified separately and lands in the same terminal state, so a dropped
 * connection mid-chunk is a visible failure rather than a silent hang.
 *
 * PER-UPLOAD STATE
 * ----------------
 * `partETags` and `uploadId` used to be MODULE-LEVEL mutable variables, shared
 * by every upload on the page: two uploads in flight interleaved each other's
 * manifests. They now live in `uploadStatesRef`, a Map keyed by file index and
 * owned by the hook instance — isolated per hook instance AND per file.
 *
 * TOASTING — the single-toast rule (recorded PM decision, TICKET-008)
 * ------------------------------------------------------------------
 * `baseQueryWithReAuth` already toasts HTTP 400/500 and any body carrying
 * errorCode '1030' GLOBALLY. So this hook does NOT add its generic failure
 * toast for a 1030 BODY_FAILURE — that failure has already been reported, and a
 * second notification just replaces the first with a vaguer one. Everything the
 * global layer leaves silent still toasts here: a non-'0'/non-'1030' body
 * (e.g. 1027), a success body missing the value we needed, and every transport
 * failure. Same rule TICKET-002 applied at the page level.
 */

/**
 * The generic custom-error code. NEVER branch business logic on it (it is
 * shared by every custom error); it is used here only to answer one question —
 * "has the global layer already toasted this?".
 */
export const CUSTOM_ERROR_CODE = '1030';

/**
 * The one business outcome of completeUpload that gets its own copy. Matched on
 * `message` — never on errorCode, which is the shared generic custom-error code.
 */
export const RESOURCE_ALREADY_EXISTS_MESSAGE = 'the resource already exist';

type UploadNotification = {
  title: string;
  description: string;
};

const GENERIC_FAILURE_NOTIFICATION: UploadNotification = {
  title: 'Something went wrong!',
  description: 'Please try this action later or contact BrickLynk support.',
};

/** Per-file multipart state. Never module-level — see the header. */
type UploadState = {
  uploadId: string | null;
  partETags: CompleteUploadData['partETags'];
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/**
 * The inner `data` an AppResponse envelope carries, or undefined. Never throws:
 * `extractResponsePayload` always hands back an object.
 */
const extractEnvelopeData = (response: unknown): unknown =>
  (extractResponsePayload(response) as { data?: unknown }).data;

/**
 * True when `baseQueryWithReAuth` has ALREADY shown the user a notification for
 * this exact result: a body-level failure carrying errorCode '1030'. Transport
 * failures are deliberately excluded — the global layer only covers HTTP
 * 400/500, and a FETCH_ERROR / 403 / 404 / timeout would otherwise fail
 * silently.
 */
export const isGloballyReportedFailure = (response: unknown): boolean =>
  classifyMutationResult(response) === MutationResult.BODY_FAILURE &&
  extractResponsePayload(response).errorCode === CUSTOM_ERROR_CODE;

/**
 * The hook-level notification for a failed step: the generic toast, or `null`
 * when the global layer already reported it (the single-toast rule).
 */
const failureNotificationFor = (
  response: unknown
): UploadNotification | null =>
  isGloballyReportedFailure(response) ? null : GENERIC_FAILURE_NOTIFICATION;

/**
 * A part is usable only if it can be sent to S3 as-is: an object with a
 * non-empty `etag` and a defined `partNumber`. This is the guard that makes
 * "undefined can never enter partETags" true — it is applied both to a fresh
 * chunk reply and to a manifest restored from localStorage (which, on a browser
 * that ran the previous build, may already be poisoned with nulls).
 *
 * `partNumber` is checked for presence rather than `typeof number` so a
 * numeric-string from the backend is still accepted; only absence is fatal.
 */
export const isUsablePartETag = (value: unknown): value is Upload['partETag'] => {
  if (!isRecord(value)) return false;
  const { etag, partNumber } = value as { etag?: unknown; partNumber?: unknown };
  return (
    typeof etag === 'string' &&
    etag.length > 0 &&
    partNumber !== undefined &&
    partNumber !== null
  );
};

/**
 * A stored `expirationDate` is JSON, so it comes back as an ISO STRING, never a
 * Date. Parsing it to epoch millis is what makes the expiry check a real
 * comparison — the previous code compared that string against a `Date` object,
 * which JS resolves by stringifying the Date ("Wed Aug 05 2026 ..."), so an ISO
 * string always sorted below it and every stored entry read as the same branch
 * whatever its date.
 */
export const parseExpirationDate = (value: unknown): number | null => {
  if (typeof value !== 'string' && typeof value !== 'number') return null;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? null : time;
};

/**
 * Await a NON-unwrapped RTK Query trigger. A trigger resolves to `{ data }` or
 * `{ error }` rather than rejecting, but a throw (an aborted call, a
 * serialisation error, a mocked failure) is normalised to the `{ error }`
 * transport shape so exactly one classification path exists.
 */
const runMutation = async (
  invoke: () => Promise<unknown>
): Promise<unknown> => {
  try {
    return await invoke();
  } catch (error) {
    return { error };
  }
};

export const useUploadFiles = (attachedId: number) => {
  const [files, setFiles] = useState<{ file: File }[]>([]);
  const [filesStatus, setFilesStatus] = useState<FileStatus[]>([]);
  const [currentFileIndex, setCurrentFileIndex] = useState<number | null>(null);
  const [lastUploadedFileIndex, setLastUploadedFileIndex] = useState<
    number | null
  >(null);
  const [currentChunkIndex, setCurrentChunkIndex] = useState<number | null>(
    null
  );
  const [fileStatusChangedIndex, setFileStatusChangedIndex] = useState<
    number | null
  >(null);

  /**
   * Per-file multipart state, owned by THIS hook instance. Replaces the
   * module-level `partETags` / `uploadId`, which every upload on the page used
   * to share. A ref (not state) because the chunk loop mutates it between
   * renders and a manifest push must never trigger a re-render.
   */
  const uploadStatesRef = useRef<Map<number, UploadState>>(new Map());

  const [initiateUpload] = useInitiateUploadMutation();
  const [uploadChunk] = useUploadMutation();
  const [completeUploadFile] = useCompleteUploadMutation();

  const moduleName = location.pathname.endsWith('new-document')
    ? 'Document'
    : 'Task';

  const resetUploadState = (fileIndex: number): UploadState => {
    const fresh: UploadState = { uploadId: null, partETags: [] };
    uploadStatesRef.current.set(fileIndex, fresh);
    return fresh;
  };

  const getUploadState = (fileIndex: number): UploadState =>
    uploadStatesRef.current.get(fileIndex) ?? resetUploadState(fileIndex);

  /**
   * The single terminal-failure exit. Marks the file Failed, stops the chunk
   * loop, and hands the queue back so the remaining files still run. The
   * manifest is deliberately NOT submitted and NOT extended.
   *
   * `notification` is null when the global layer already reported the failure.
   */
  const failUpload = (
    fileIndex: number,
    notification: UploadNotification | null = GENERIC_FAILURE_NOTIFICATION
  ) => {
    onChangeFileStatus(fileIndex, FileStatus.Failed);
    setCurrentChunkIndex(null);
    setLastUploadedFileIndex(fileIndex);

    if (notification) {
      displayNotification({ ...notification, status: 'error' });
    }
  };

  const splitFileAndUploadCurrentChunk = async () => {
    if (currentChunkIndex === null || currentFileIndex === null) return;
    const currentFile = files[currentFileIndex];
    if (!currentFile) return;
    const { file } = currentFile;

    const chunkSize = calculateChunkSize(file.size);

    const from = currentChunkIndex * chunkSize;
    const to = from + chunkSize;
    const blob = file.slice(from, to);
    uploadCurrentChunk(blob);
  };

  const uploadCurrentChunk = async (blob: Blob) => {
    if (currentChunkIndex === null || currentFileIndex === null) return;
    const fileIndex = currentFileIndex;
    const chunkIndex = currentChunkIndex;
    const currentFile = files[fileIndex];
    if (!currentFile) return;
    const { file } = currentFile;

    if (
      ![FileStatus.InProgress, FileStatus.Resumed].includes(
        filesStatus[fileIndex]
      )
    ) {
      setLastUploadedFileIndex(fileIndex);
      setCurrentChunkIndex(null);
      return;
    }

    const uploadState = getUploadState(fileIndex);
    const activeUploadId = uploadState.uploadId;

    // The uploadId is NEVER read while undefined: without one there is no
    // multipart upload to attach a part to, so the file is Failed outright.
    // Nothing was requested, so nothing was reported globally — toast.
    if (!activeUploadId) {
      failUpload(fileIndex);
      return;
    }

    const formData = new FormData();
    formData.append('file', blob);
    formData.append(
      'partMetadata',
      JSON.stringify({
        uploadId: activeUploadId,
        fileName: file.name,
        attachedId,
        partNumber: chunkIndex + 1,
      })
    );
    formData.append('moduleName', moduleName);

    const chunkResult = await runMutation(() => uploadChunk(formData));
    const chunkOutcome = classifyMutationResult(chunkResult);
    const partETag = (extractEnvelopeData(chunkResult) as Upload | undefined)
      ?.partETag;

    // A chunk reply that is not a body-level success carrying a usable
    // partETag aborts the file. Nothing is pushed, so `undefined` can never
    // reach the manifest, and the loop does not advance to the next chunk.
    if (chunkOutcome !== MutationResult.SUCCESS || !isUsablePartETag(partETag)) {
      failUpload(fileIndex, failureNotificationFor(chunkResult));
      return;
    }

    uploadState.partETags.push(partETag);

    const isLastChunk =
      chunkIndex === Math.ceil(file.size / calculateChunkSize(file.size)) - 1;

    if (!isLastChunk) {
      setCurrentChunkIndex(chunkIndex + 1);
      return;
    }

    const completeResult = await runMutation(() =>
      completeUploadFile({
        moduleName,
        data: {
          uploadId: activeUploadId,
          partETags: uploadState.partETags,
          fileName: file.name,
          fileSize: file.size,
          attachedId,
          documentTags: [],
        },
      })
    );

    const completeOutcome = classifyMutationResult(completeResult);
    const completePayload = extractResponsePayload(completeResult);

    // Explicit named case, preserved from the previous implementation. Checked
    // BEFORE the success branch: the backend has been observed returning this
    // message inside an otherwise well-formed envelope, and it must never be
    // mistaken for a completed upload whichever errorCode carries it.
    //
    // Deliberately EXEMPT from the single-toast rule: this is not the generic
    // failure toast the rule is about — it is specific copy naming the actual
    // outcome ("Cannot upload the document"), which is more useful than the raw
    // backend message the global layer shows, and it must survive whatever
    // errorCode the message arrives under.
    if (completePayload.message === RESOURCE_ALREADY_EXISTS_MESSAGE) {
      failUpload(fileIndex, {
        title: 'Cannot upload the document',
        description: completePayload.message || 'Document already exist',
      });
      return;
    }

    // Anything that is not a body-level success is a FAILURE, never Completed.
    if (completeOutcome !== MutationResult.SUCCESS) {
      failUpload(fileIndex, failureNotificationFor(completeResult));
      return;
    }

    onChangeFileStatus(fileIndex, FileStatus.Completed);
    setCurrentChunkIndex(null);
    setLastUploadedFileIndex(fileIndex);
  };

  const uploadFileForFirstTime = async () => {
    if (currentFileIndex === null) return;
    const fileIndex = currentFileIndex;
    const currentFile = files[fileIndex];
    if (!currentFile) return;
    const { file } = currentFile;

    // Fresh per-file state: no leftovers from a previous attempt at this index.
    const uploadState = resetUploadState(fileIndex);

    const initiateResult = await runMutation(() =>
      initiateUpload({
        moduleName,
        data: {
          fileName: file.name,
          attachedId,
        },
      })
    );

    const initiateOutcome = classifyMutationResult(initiateResult);
    const initiatedData = extractEnvelopeData(initiateResult);
    const newUploadId =
      isRecord(initiatedData) &&
      typeof initiatedData.uploadId === 'string' &&
      initiatedData.uploadId.length > 0
        ? initiatedData.uploadId
        : null;

    // An initiate failure NEVER enters the chunk loop: setCurrentChunkIndex(0)
    // is only reached with a real uploadId in hand.
    if (initiateOutcome !== MutationResult.SUCCESS || !newUploadId) {
      failUpload(fileIndex, failureNotificationFor(initiateResult));
      return;
    }

    uploadState.uploadId = newUploadId;
    setCurrentChunkIndex(0);
  };

  const onChangeFileStatus = (fileIndex: number, newStatus: FileStatus) => {
    setFileDataInLocalStorage(fileIndex, newStatus);

    setFilesStatus(prevFilesStatus => {
      setFileStatusChangedIndex(fileIndex);
      return prevFilesStatus.map((status, fileStatusIndex) => {
        if (fileStatusIndex === fileIndex) return newStatus;
        else return status;
      });
    });
  };

  /**
   * The localStorage key for a file, or null when there is no such file. One
   * builder so the write, the read and the eviction can never drift apart.
   */
  const getFileStorageKey = (fileIndex: number | null): string | null => {
    if (fileIndex === null) return null;
    const currentFile = files[fileIndex];
    if (!currentFile) return null;
    const {
      file: { name, size },
    } = currentFile;

    return `${moduleName}_${attachedId}_${name}_${size}`;
  };

  const setFileDataInLocalStorage = (
    fileIndex: number | null,
    status: FileStatus
  ) => {
    if (status !== FileStatus.Paused) return;

    // Index 0 is a valid file index. The previous guard was
    // `status === Paused && fileIndex`, and 0 is falsy — so the FIRST file in
    // every queue silently never persisted its resume state and could not be
    // resumed after a reload.
    const fileStorageKey = getFileStorageKey(fileIndex);
    if (fileStorageKey === null || fileIndex === null) return;

    const { tomorrow } = getTomorrowsDate();

    // Per-file state, read by index — no longer "whatever the module-level
    // variables happen to hold for whichever upload ran last".
    const uploadState = uploadStatesRef.current.get(fileIndex);

    localStorage.setItem(
      fileStorageKey,
      JSON.stringify({
        uploadId: uploadState?.uploadId ?? null,
        partETags: uploadState?.partETags ?? [],
        attachedId,
        currentChunkIndex:
          fileIndex === currentFileIndex ? Number(currentChunkIndex) - 1 : null,
        expirationDate: tomorrow,
      })
    );
  };

  const getFileDataFromLocalStorage = (fileIndex: number) => {
    const fileStorageKey = getFileStorageKey(fileIndex);
    if (fileStorageKey === null) return null;

    return localStorage.getItem(fileStorageKey);
  };

  const removeFileDataFromLocalStorage = (fileIndex: number) => {
    const fileStorageKey = getFileStorageKey(fileIndex);
    if (fileStorageKey === null) return;

    localStorage.removeItem(fileStorageKey);
  };

  /**
   * Is there a stored entry worth resuming from?
   *
   * INTENT, re-derived rather than inherited: an entry that has EXPIRED is
   * useless (S3 has aborted the multipart upload) — evict it and start fresh.
   * An entry still inside its window is offered for resume. The previous
   * implementation got both halves wrong: it compared the JSON-revived
   * expiration STRING against a `Date` object (a stringified-Date comparison
   * that is true for every ISO date), and it returned `true` — "resume this" —
   * from the branch it labelled expired, while evicting the entries that were
   * still fresh.
   */
  const checkDataInLocalStorage = (fileIndex: number): boolean => {
    const fileDataJSON = getFileDataFromLocalStorage(fileIndex);
    if (!fileDataJSON) return false;

    let fileData: unknown;
    try {
      fileData = JSON.parse(fileDataJSON);
    } catch {
      removeFileDataFromLocalStorage(fileIndex);
      return false;
    }

    const expiresAt = isRecord(fileData)
      ? parseExpirationDate(fileData.expirationDate)
      : null;

    // Unparseable is treated exactly like expired: fail closed, because a
    // resume we cannot date is a resume we cannot trust.
    if (expiresAt === null || expiresAt <= Date.now()) {
      removeFileDataFromLocalStorage(fileIndex);
      return false;
    }

    return true;
  };

  /**
   * Restore a resumable upload into THIS file's per-upload state. Returns false
   * when there is nothing trustworthy to resume from, so the caller can start a
   * fresh upload instead of entering the chunk loop with a missing uploadId or
   * a broken chunk index.
   */
  const setDataFromLocalStorage = (fileIndex: number): boolean => {
    const fileDataJSON = getFileDataFromLocalStorage(fileIndex);
    if (!fileDataJSON) return false;

    let fileData: unknown;
    try {
      fileData = JSON.parse(fileDataJSON);
    } catch {
      return false;
    }

    if (!isRecord(fileData)) return false;
    if (typeof fileData.uploadId !== 'string' || !fileData.uploadId) {
      return false;
    }

    const restoredChunkIndex = Number(fileData.currentChunkIndex);
    if (!Number.isInteger(restoredChunkIndex) || restoredChunkIndex < 0) {
      return false;
    }

    const uploadState = resetUploadState(fileIndex);
    uploadState.uploadId = fileData.uploadId;
    // A manifest persisted by the previous build can contain nulls; anything
    // unusable is dropped rather than re-submitted to S3.
    uploadState.partETags = Array.isArray(fileData.partETags)
      ? fileData.partETags.filter(isUsablePartETag)
      : [];

    setCurrentChunkIndex(restoredChunkIndex);
    return true;
  };

  useEffect(() => {
    if (lastUploadedFileIndex === null || currentFileIndex === null) return;

    const isLastFile = lastUploadedFileIndex === files.length - 1;
    let nextFileIndex: number | null;

    if (isLastFile) nextFileIndex = null;
    else {
      const index = files.findIndex(
        (_, fileIndex) =>
          [FileStatus.InProgress, FileStatus.Resumed].includes(
            filesStatus[fileIndex]
          ) && fileIndex > currentFileIndex
      );

      nextFileIndex = index;
    }

    setCurrentFileIndex(nextFileIndex);
  }, [lastUploadedFileIndex]);

  useEffect(() => {
    if (files.length > 0) {
      if (currentFileIndex === null) {
        setCurrentFileIndex(
          lastUploadedFileIndex === null ? 0 : lastUploadedFileIndex + 1
        );
      }

      setFilesStatus(prevFilesStatus => {
        return files.map((_, fileIndex) => {
          if (prevFilesStatus[fileIndex]) return prevFilesStatus[fileIndex];
          else return FileStatus.InProgress;
        });
      });
    }
  }, [files.length]);

  useEffect(() => {
    if (currentFileIndex !== null) {
      if (
        [FileStatus.InProgress, FileStatus.Resumed].includes(
          filesStatus[currentFileIndex]
        )
      ) {
        const isFileStored = checkDataInLocalStorage(currentFileIndex);
        // A stored entry that cannot be trusted falls back to a fresh upload
        // rather than leaving the file stranded mid-queue.
        if (!isFileStored || !setDataFromLocalStorage(currentFileIndex)) {
          uploadFileForFirstTime();
        }
      } else {
        setLastUploadedFileIndex(currentFileIndex);
        setCurrentChunkIndex(null);
      }
    }
  }, [currentFileIndex]);

  useEffect(() => {
    if (currentChunkIndex !== null) splitFileAndUploadCurrentChunk();
  }, [currentChunkIndex]);

  useEffect(() => {
    if (
      fileStatusChangedIndex !== null &&
      filesStatus[fileStatusChangedIndex] !== FileStatus.Completed &&
      // A FAILED file must not rewind the queue to index 0 (the pause/cancel
      // re-scan): it is terminal, so the lastUploadedFileIndex effect above
      // moves straight on to the next pending file.
      filesStatus[fileStatusChangedIndex] !== FileStatus.Failed
    ) {
      setFileDataInLocalStorage(
        currentFileIndex,
        filesStatus[currentFileIndex]
      );

      setCurrentFileIndex(0);
    }
  }, [JSON.stringify(filesStatus)]);

  return {
    files,
    setFiles,
    currentFileIndex,
    currentChunkIndex,
    filesStatus,
    onChangeFileStatus,
  };
};
